// THE RESPONSIBLE PARTNER COMES FIRST, AND IS MARKED — the display half of
// brief 48 §2's ruling.
//
// Laurent's words were *"add RP always as first member of the case team so it
// is clear and the other features can rely on it."* The DATA half of that is
// server-side (`server/src/lib/caseTeam.js`): the RP is a real team row now, so
// every reader of `caseCollaborators` gets them without asking. This is the
// other half, and it is genuinely a different question — a row that is present
// but sixth in a list of avatars is not "clear".
//
// It lives here rather than in either view because both case and collective
// case render the same panel and the ruling is one ruling. A sort written twice
// is two behaviours waiting to disagree.
//
// `isRp` rides on the entry rather than being recomputed at the badge: the
// caller already has the RP id list and the renderer should not have to.

// `unknown` and not `any` in the index signature: a team entry carries fields
// this file has no business knowing about (the wire shape has grown three
// times), and they must ride through the sort untouched without becoming a
// licence to dereference them here.
export type CollaboratorId = number | string | null | undefined;
export type TeamEntry = {
  coId?: CollaboratorId;
  ratio?: number | null;
  role?: string | null;
  [k: string]: unknown;
};
export type RankedTeamEntry = TeamEntry & { isRp: boolean };

/**
 * The team, responsible partners first, everyone else in the order they came.
 *
 * STABLE ON BOTH SIDES — this never reorders anybody relative to anybody else
 * within their group, because that order is the firm's own and nothing here has
 * an opinion about it. Two RPs therefore surface in the order the TEAM holds
 * them, not the order the `responsiblePartners` field lists them: that field's
 * order means something elsewhere (several readers treat `[0]` as the primary
 * RP) and importing that meaning here would be a ruling nobody has made.
 */
export function teamWithRpFirst(
  entries: readonly TeamEntry[] | null | undefined,
  rpIds: readonly CollaboratorId[] | null | undefined,
): RankedTeamEntry[] {
  const rp = new Set((Array.isArray(rpIds) ? rpIds : []).map(Number).filter((n) => Number.isFinite(n)));
  const ranked = (Array.isArray(entries) ? entries : []).map((e) => ({
    ...e,
    isRp: rp.has(Number(e?.coId)),
  }));
  // A stable partition, not a comparator: `Array.prototype.sort` is only
  // guaranteed stable per spec, and a partition says what is meant.
  return [...ranked.filter((e) => e.isRp), ...ranked.filter((e) => !e.isRp)];
}

/**
 * What marks an RP's avatar. `outline` and not `boxShadow`, deliberately:
 * InitialsBadge already spends `boxShadow` on the "this is me" ring
 * (`SharedUI.tsx:334`), and a person who is both would otherwise lose one of
 * the two marks to whichever style object was spread last.
 */
export const RP_BADGE_STYLE = {
  outline: "1.5px solid var(--am)",
  outlineOffset: "1.5px",
} as const;
